import { runPrompt, DEFAULT_COPILOT_URL } from '@/api/ai-agent.api'
import {
  getStoredSessions,
  saveStoredSessions
} from '@/api/ai-agent.storage'

// The in-flight request's AbortController is intentionally kept out of Vuex
// state. It is not serializable, and the dev logger/strict plugins would try
// to clone it on every mutation. State only needs the isStreaming flag.
let abortController = null

// Guard against a runaway queue. Ten is far past normal use, and the composer
// says so rather than dropping prompts on the floor.
export const MAX_QUEUED_PROMPTS = 10

function makeId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }

  return Math.random().toString(36).slice(2, 10)
}

// A session's title is just its first user prompt, trimmed to one line so the
// history list stays scannable.
function deriveTitle(messages) {
  const firstUser = (messages || []).find(m => m.role === 'user')
  const text = (firstUser && firstUser.text ? firstUser.text : '').trim()
  if (!text) {
    return 'New chat'
  }

  const oneLine = text.replace(/\s+/g, ' ')
  return oneLine.length > 60 ? oneLine.slice(0, 59) + '…' : oneLine
}

// The history list only needs lightweight metadata; the full message array
// lives in storage and is read on demand when a session is opened.
function toMeta(session) {
  return {
    id: session.id,
    title: session.title || 'New chat',
    createdAt: session.createdAt,
    updatedAt: session.updatedAt,
    messageCount: Array.isArray(session.messages) ? session.messages.length : (session.messageCount || 0)
  }
}

// Every assistant message is created with the same shape so templates can
// rely on arrays existing without optional-chaining everywhere.
//
// `timeline` is the chronological record of the turn: text blocks and tool
// calls in the order they happened. `answer` and `tools` are kept in step with
// it because thread history, the chart extractor and sessions saved before
// timeline existed all read those two.
// `prompt` is the turn that triggered it, copied here so a failure report can
// quote the request without walking back through the transcript.
function newAssistantMessage(now, prompt) {
  return {
    id: makeId(),
    role: 'assistant',
    prompt: prompt || '',
    plan: '',
    tools: [],
    answer: '',
    timeline: [],
    citations: [],
    at: now,
    lastAt: null,
    status: 'streaming',
    error: null
  }
}

// The tail text block of a turn, if the turn is still adding to it. A tool call
// closes the block, so anything after a tool starts a fresh one.
function openTextEntry(timeline) {
  const last = timeline[timeline.length - 1]
  return last && last.kind === 'text' && !last.closed ? last : null
}

function createState() {
  return {
    panelOpen: false,
    messages: [],
    isStreaming: false,
    // Prompts typed while a turn was in flight, oldest first. They are joined
    // into one request when the current turn finishes.
    queuedPrompts: [],
    // Submitted prompts, oldest first, for shell-style ↑/↓ recall in the
    // composer. Survives clearChat on purpose, like real terminal history.
    promptHistory: [],
    // Identity used to scope saved sessions to the logged-in admin. Resolved
    // lazily from the auth module when the panel opens.
    userId: '',
    // The session the current `messages` belong to. null means "unsaved /
    // brand-new chat" until the first prompt creates one.
    currentSessionId: null,
    // Lightweight metadata for the history list: { id, title, createdAt,
    // updatedAt, messageCount }. Full transcripts are read from storage.
    sessions: []
  }
}

const state = createState()

// Mutations are pure — no localStorage, no network. Side effects live in
// actions. This keeps dev-mode strict-mode happy and makes mutations
// trivially testable in isolation.
const mutations = {
  setPanelOpen(state, open) {
    state.panelOpen = open
  },
  togglePanel(state) {
    state.panelOpen = !state.panelOpen
  },
  pushMessage(state, message) {
    state.messages.push(message)
  },
  patchMessage(state, { id, patch }) {
    const idx = state.messages.findIndex(m => m.id === id)
    if (idx === -1) {
      return
    }

    state.messages.splice(idx, 1, { ...state.messages[idx], ...patch })
  },
  appendPlan(state, { id, text }) {
    const idx = state.messages.findIndex(m => m.id === id)
    if (idx === -1) {
      return
    }

    const msg = state.messages[idx]
    state.messages.splice(idx, 1, { ...msg, plan: (msg.plan || '') + text })
  },
  startTool(state, { id, toolId, name, args }) {
    const idx = state.messages.findIndex(m => m.id === id)
    if (idx === -1) {
      return
    }

    const msg = state.messages[idx]
    const tool = { id: toolId, name, args, result: null, status: 'running' }
    // A tool call ends the text block in front of it. Without this the next
    // model turn continues the same paragraph and its markdown breaks.
    const timeline = (msg.timeline || []).map(e =>
      e.kind === 'text' && !e.closed ? { ...e, closed: true } : e
    )
    state.messages.splice(idx, 1, {
      ...msg,
      tools: [...msg.tools, tool],
      timeline: [...timeline, { kind: 'tool', ...tool }]
    })
  },
  finishTool(state, { id, toolId, result }) {
    const idx = state.messages.findIndex(m => m.id === id)
    if (idx === -1) {
      return
    }

    const msg = state.messages[idx]
    const tools = msg.tools.map(t => t.id === toolId ? { ...t, result, status: 'done' } : t)
    const timeline = (msg.timeline || []).map(e =>
      e.kind === 'tool' && e.id === toolId ? { ...e, result, status: 'done' } : e
    )
    state.messages.splice(idx, 1, { ...msg, tools, timeline })
  },
  appendAnswer(state, { id, text, segment }) {
    const idx = state.messages.findIndex(m => m.id === id)
    if (idx === -1) {
      return
    }

    const msg = state.messages[idx]
    const timeline = [...(msg.timeline || [])]
    // A message carrying answer text but no timeline comes from a session saved
    // before the timeline existed. Adopt its text as the open block so a resumed
    // conversation keeps appending instead of starting a second block.
    if (!timeline.length && msg.answer) {
      timeline.push({ kind: 'text', id: makeId(), segment, text: msg.answer, closed: false })
    }

    const open = openTextEntry(timeline)
    // A changed segment is the backend saying "new model turn" even when no tool
    // ran in between. Falls back to the tool boundary when the field is absent.
    const sameTurn = open && (segment === undefined || open.segment === undefined || open.segment === segment)

    if (sameTurn) {
      timeline.splice(timeline.length - 1, 1, { ...open, text: open.text + text })
    } else {
      if (open) {
        timeline.splice(timeline.length - 1, 1, { ...open, closed: true })
      }

      timeline.push({ kind: 'text', id: makeId(), segment, text, closed: false })
    }

    const answer = sameTurn || !msg.answer
      ? (msg.answer || '') + text
      : msg.answer + '\n\n' + text

    state.messages.splice(idx, 1, { ...msg, answer, timeline })
  },
  setCitations(state, { id, items }) {
    const idx = state.messages.findIndex(m => m.id === id)
    if (idx === -1) {
      return
    }

    const msg = state.messages[idx]
    state.messages.splice(idx, 1, { ...msg, citations: Array.isArray(items) ? items : [] })
  },
  setStreaming(state, value) {
    state.isStreaming = value
  },
  queuePrompt(state, text) {
    const trimmed = (text || '').trim()
    if (!trimmed || state.queuedPrompts.length >= MAX_QUEUED_PROMPTS) {
      return
    }

    state.queuedPrompts = [...state.queuedPrompts, trimmed]
  },
  removeQueuedPrompt(state, index) {
    if (index < 0 || index >= state.queuedPrompts.length) {
      return
    }

    const next = [...state.queuedPrompts]
    next.splice(index, 1)
    state.queuedPrompts = next
  },
  clearQueue(state) {
    if (state.queuedPrompts.length) {
      state.queuedPrompts = []
    }
  },
  clearMessages(state) {
    state.messages = []
  },
  setMessages(state, list) {
    state.messages = Array.isArray(list) ? list : []
  },
  setUserId(state, id) {
    state.userId = id || ''
  },
  setCurrentSessionId(state, id) {
    state.currentSessionId = id || null
  },
  setSessions(state, list) {
    state.sessions = Array.isArray(list) ? list : []
  },
  upsertSessionMeta(state, meta) {
    if (!Array.isArray(state.sessions)) {
      state.sessions = []
    }

    const idx = state.sessions.findIndex(s => s.id === meta.id)
    if (idx === -1) {
      state.sessions.unshift(meta)
    } else {
      state.sessions.splice(idx, 1, { ...state.sessions[idx], ...meta })
    }
  },
  pushHistory(state, text) {
    const trimmed = (text || '').trim()
    // Skip blanks and consecutive duplicates so ↑ doesn't stutter on repeats.
    if (!trimmed || state.promptHistory[state.promptHistory.length - 1] === trimmed) {
      return
    }

    state.promptHistory.push(trimmed)
    // Keep only the most recent 100 entries.
    if (state.promptHistory.length > 100) {
      state.promptHistory.shift()
    }
  }
}

// Keep requests bounded: only the most recent turns go to the backend. Older
// context is dropped first; the full transcript stays in the UI/session store.
const MAX_THREAD_HISTORY_MESSAGES = 20

function buildThreadHistory(messages) {
  const history = []
  for (const msg of messages) {
    if (msg.role === 'user') {
      history.push({ role: 'user', content: msg.text })
    } else if (msg.role === 'assistant') {
      const text = (msg.plan ? msg.plan + '\n\n' : '') + (msg.answer || '')
      if (text.trim()) {
        history.push({ role: 'assistant', content: text.trim() })
      }
    }
  }

  return history.slice(-MAX_THREAD_HISTORY_MESSAGES)
}

const actions = {
  openPanel({ commit, dispatch }) {
    dispatch('syncUser')
    commit('setPanelOpen', true)
  },
  closePanel({ commit }) {
    commit('setPanelOpen', false)
  },
  async togglePanel({ commit, state, dispatch }) {
    if (state.panelOpen) {
      commit('setPanelOpen', false)
    } else {
      await dispatch('openPanel')
    }
  },

  // Resolve the logged-in admin and load *their* saved sessions. When the user
  // changes (or first resolves), drop any in-memory conversation so one admin
  // never sees another's chat carried over in the same tab.
  syncUser({ commit, state, dispatch, rootGetters }) {
    const uid = rootGetters['auth/loggedInUserID'] || ''
    if (uid === state.userId) {
      return
    }

    commit('setUserId', uid)
    commit('clearMessages')
    commit('setCurrentSessionId', null)
    dispatch('loadUserSessions')
  },

  loadUserSessions({ commit, state }) {
    commit('setSessions', getStoredSessions(state.userId).map(toMeta))
  },

  // Make sure there's a session id to attach the current exchange to. Kept as
  // an action (not an inline commit in sendPrompt) so unit tests that stub
  // `dispatch` stay isolated from session/storage side effects.
  ensureSession({ commit, state, rootGetters }, firstPrompt) {
    if (!state.userId) {
      commit('setUserId', rootGetters['auth/loggedInUserID'] || '')
    }

    if (state.currentSessionId) {
      return
    }

    const now = Date.now()
    const id = makeId()
    commit('setCurrentSessionId', id)
    commit('upsertSessionMeta', {
      id,
      title: deriveTitle([{ role: 'user', text: firstPrompt }]),
      createdAt: now,
      updatedAt: now,
      messageCount: 0
    })
  },

  // Write the current conversation back to the user's session store. Called at
  // the end of each completed exchange, so a reload between turns keeps history.
  persistCurrentSession({ commit, state }) {
    if (!state.currentSessionId || !state.messages.length) {
      return
    }

    const all = getStoredSessions(state.userId)
    const existing = all.find(s => s.id === state.currentSessionId)
    const session = {
      id: state.currentSessionId,
      title: deriveTitle(state.messages),
      createdAt: existing ? existing.createdAt : Date.now(),
      updatedAt: Date.now(),
      messages: state.messages
    }

    const next = [session, ...all.filter(s => s.id !== state.currentSessionId)]
    const saved = saveStoredSessions(state.userId, next)
    commit('setSessions', saved.map(toMeta))
  },

  loadSession({ commit, state }, id) {
    if (abortController) {
      abortController.abort()
    }

    commit('clearQueue')
    commit('setStreaming', false)

    const session = getStoredSessions(state.userId).find(s => s.id === id)
    if (!session) {
      return
    }

    // A transcript saved mid-stream could carry a 'streaming' status that would
    // otherwise spin forever once reloaded — settle those to 'done'.
    const messages = (session.messages || []).map(m =>
      m.role === 'assistant' && m.status === 'streaming'
        ? { ...m, status: 'done', lastAt: m.lastAt || m.at || Date.now() }
        : m
    )

    commit('setMessages', messages)
    commit('setCurrentSessionId', id)
  },

  deleteSession({ commit, state }, id) {
    const remaining = getStoredSessions(state.userId).filter(s => s.id !== id)
    const saved = saveStoredSessions(state.userId, remaining)
    commit('setSessions', saved.map(toMeta))

    if (state.currentSessionId === id) {
      commit('clearMessages')
      commit('setCurrentSessionId', null)
    }
  },

  // "New chat" — the previous conversation is already saved to history at each
  // completed exchange, so this just resets the live thread to a blank session.
  clearChat({ commit }) {
    if (abortController) {
      abortController.abort()
    }

    commit('clearQueue')
    commit('clearMessages')
    commit('setCurrentSessionId', null)
  },
  // Stop means stop: anything lined up behind the current turn is dropped too,
  // otherwise aborting would immediately fire the queue instead of stopping.
  stopStreaming({ commit }) {
    commit('clearQueue')

    if (abortController) {
      abortController.abort()
    }
  },

  // What the composer calls. Runs the prompt now, or lines it up behind the turn
  // already in flight. The rule lives here so the panel never has to check
  // whether a request is live before deciding what to do.
  submitPrompt({ commit, state, dispatch }, prompt) {
    const text = (prompt || '').trim()
    if (!text) {
      return
    }

    if (state.isStreaming) {
      commit('queuePrompt', text)
      commit('pushHistory', text)
      return
    }

    return dispatch('sendPrompt', text)
  },

  removeQueuedPrompt({ commit }, index) {
    commit('removeQueuedPrompt', index)
  },

  async sendPrompt({ commit, state, dispatch }, prompt) {
    const text = (prompt || '').trim()
    if (!text || state.isStreaming) {
      return
    }

    commit('pushHistory', text)

    // Held across the whole drain, not per turn: flipping it between queued
    // turns would blink the composer and the "Working on it" bar.
    commit('setStreaming', true)

    let next = text
    while (next) {
      await dispatch('runTurn', next)

      // Everything typed during that turn goes out as a single request, which is
      // both cheaper and gives the model all the follow-ups at once.
      next = state.queuedPrompts.join('\n').trim()
      commit('clearQueue')
    }

    commit('setStreaming', false)
  },

  async runTurn({ commit, state, rootGetters, dispatch }, text) {
    // Attach this exchange to a session (creating one on the first prompt).
    await dispatch('ensureSession', text)

    const now = Date.now()
    commit('pushMessage', { id: makeId(), role: 'user', text, at: now })

    const assistant = newAssistantMessage(now, text)
    commit('pushMessage', assistant)

    abortController = new AbortController()

    const threadHistory = buildThreadHistory(state.messages.slice(0, -2))

    // The backend always emits `done` after an `error` (its finally block). Track
    // whether we errored so the trailing `done` doesn't overwrite the error
    // status — otherwise the failure is swallowed and the user sees a blank reply.
    let errored = false

    await runPrompt(text, {
      signal: abortController.signal,
      threadHistory,
      // Keys the AgentCore runtime session so every turn of this chat lands on
      // the same instance. ensureSession above guarantees the id exists.
      conversationId: state.currentSessionId,
      accessToken: rootGetters['auth/accessToken'],
      refreshAccessToken: async () => {
        await dispatch('auth/refreshAccessToken', null, { root: true })
        return rootGetters['auth/accessToken']
      },
      onEvent: (ev) => {
        switch (ev.type) {
          case 'plan_chunk':
            commit('appendPlan', { id: assistant.id, text: ev.text })
            break
          case 'tool_start':
            commit('startTool', { id: assistant.id, toolId: ev.id, name: ev.name, args: ev.args })
            break
          case 'tool_result':
            commit('finishTool', { id: assistant.id, toolId: ev.id, result: ev.result })
            break
          case 'answer_chunk':
            commit('appendAnswer', { id: assistant.id, text: ev.text, segment: ev.segment })
            break
          case 'citations':
            commit('setCitations', { id: assistant.id, items: ev.items })
            break
          case 'done':
            if (!errored) {
              commit('patchMessage', {
                id: assistant.id,
                patch: { status: 'done', lastAt: Date.now() }
              })
            }

            break
          case 'error':
            errored = true
            commit('patchMessage', {
              id: assistant.id,
              patch: {
                status: 'error',
                error: ev.message,
                lastAt: Date.now(),
                // Which endpoint and which backend session, recorded at failure
                // time. The error card cannot reach either on its own.
                context: {
                  endpoint: DEFAULT_COPILOT_URL,
                  conversationId: state.currentSessionId
                }
              }
            })
            break
        }
      }
    })

    abortController = null

    // Save the completed exchange so it survives reloads and shows in history.
    dispatch('persistCurrentSession')
  }
}

const getters = {
  hasMessages: state => state.messages.length > 0,
  sortedSessions: state => [...(state.sessions || [])].sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0)),
  lastMessageSignature: state => {
    // Cheap fingerprint a watcher can subscribe to without going deep.
    // Changes whenever the streaming tip grows. O(1) per mutation.
    if (state.messages.length === 0) {
      return ''
    }

    const last = state.messages[state.messages.length - 1]
    const planLen = (last.plan || '').length
    const ansLen = (last.answer || '').length
    const toolCount = (last.tools || []).length
    return `${state.messages.length}|${last.id}|${last.status}|${planLen}|${ansLen}|${toolCount}`
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}
